import { isExpenseReturnReason } from '../enums/expense-return-reason.enum';
import { isExpenseStatus } from '../enums/expense-status.enum';
import {
  ExpenseDto,
  ExpenseDriverOptionDto,
  ExpensesPaginatedRequestDto,
  ExpensesPaginatedResponseDto,
} from '../models/expense-api.model';
import {
  Expense,
  ExpenseDriverOption,
  ExpenseReview,
  ExpensesPage,
  ExpensesQuery,
} from '../models/expense.model';
import { unitsToCents } from '../utils/money.util';

/**
 * Única frontera entre la forma de la API y el modelo de dominio.
 *
 * Todas las funciones son puras y toleran datos incompletos: en desarrollo, 99
 * de cada 100 filas son histórico con `Status`, `ReportedAt`, `DriverName` y
 * `Description` a null, así que degradar bien no es un caso excepcional.
 */

const CENTS_PER_UNIT = 100;

/**
 * Interpreta la fecha **como hora local**, sin conversión de zona.
 *
 * `new Date('2026-08-14')` la trataría como medianoche UTC y en husos negativos
 * mostraría el día anterior; y un `Date` construido desde una cadena con `Z`
 * desplazaría la hora. El backend entrega la hora local del servicio sin offset,
 * así que se extraen los componentes y se construye la fecha con ellos.
 */
export function parseLocalDateTime(value: string | null): Date | null {
  if (!value) {
    return null;
  }

  const match =
    /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2})(?::(\d{2}))?)?/.exec(value);

  if (!match) {
    return null;
  }

  const [, year, month, day, hours, minutes, seconds] = match;

  return new Date(
    Number(year),
    Number(month) - 1,
    Number(day),
    Number(hours ?? 0),
    Number(minutes ?? 0),
    Number(seconds ?? 0)
  );
}

/** Formatea una fecha como `YYYY-MM-DD` local, para los filtros del listado. */
export function toApiDate(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');

  return `${date.getFullYear()}-${month}-${day}`;
}

/** Importe en céntimos → decimal con punto, como exige el multipart. */
export function centsToApiAmount(cents: number): string {
  return (cents / CENTS_PER_UNIT).toFixed(2);
}

/**
 * Revisión del gasto, o `null` si no se ha tomado ninguna decisión negativa.
 *
 * Hay dos formas de tener motivo y se admiten las dos: el código del catálogo,
 * que es lo que llega desde que existe, y sólo el texto, que es lo que llevan
 * los gastos devueltos antes de eso. Un código que no esté en el catálogo se
 * descarta en lugar de propagarse: la UI lo traduciría a una clave inexistente.
 */
function toReview(dto: ExpenseDto): ExpenseReview | null {
  const returnReasonCode = isExpenseReturnReason(dto.ReturnReasonCode)
    ? dto.ReturnReasonCode
    : null;

  if (!returnReasonCode && !dto.RejectionReason) {
    return null;
  }

  return {
    returnReasonCode,
    rejectionReason: dto.RejectionReason ?? null,
  };
}

export function toExpense(dto: ExpenseDto): Expense {
  return {
    expenseId: dto.ExpenseId ?? null,
    nodeId: dto.NodeId ?? '',
    orderHash: dto.OrderHash ?? '',
    zoneId: dto.ZoneId ?? null,
    clientId: dto.ClientId ?? null,
    clientName: dto.ClientName ?? null,
    driverId: dto.DriverId ?? null,
    driverName: dto.DriverName ?? null,
    licensePlate: dto.LicensePlate ?? null,
    category: dto.Category ?? '',
    description: dto.Description ?? null,
    amountInCents: Number.isFinite(dto.Amount) ? unitsToCents(dto.Amount) : 0,
    currencyCode: dto.CurrencyCode || 'EUR',
    status: isExpenseStatus(dto.Status) ? dto.Status : null,
    reportedAt: parseLocalDateTime(dto.ReportedAt),
    receiptUrl: dto.ReceiptUrl ?? null,
    review: toReview(dto),
    // Sólo el `false` explícito significa "no se le factura"; ausente o null es que se factura.
    isRebilledToClient:
      typeof dto.IsRebilledToClient === 'boolean'
        ? dto.IsRebilledToClient
        : null,
  };
}

export function toExpensesPage(
  dto: ExpensesPaginatedResponseDto
): ExpensesPage {
  const items = (dto.Expenses ?? []).map(toExpense);

  return {
    items,
    // Si el backend no informa el total, la paginación degrada al tamaño de la
    // página recibida en lugar de quedarse a 0 y ocultar los resultados.
    totalCount: Number.isFinite(dto.TotalItems) ? dto.TotalItems : items.length,
  };
}

/**
 * Construye el cuerpo del listado en PascalCase.
 *
 * El contrato ya no distingue mayúsculas en las claves —antes en camelCase
 * respondía 200 e ignoraba los filtros—, pero se sigue enviando en PascalCase
 * porque es la forma documentada. Hay tests que fijan las claves exactas.
 */
export function toPaginatedRequest(
  query: ExpensesQuery
): ExpensesPaginatedRequestDto {
  const searchTerm = query.searchTerm.trim();

  return {
    ...(searchTerm ? { SearchTerm: searchTerm } : {}),
    // Ausente equivale a «sin límite»: no se manda cadena vacía.
    ...(query.fromDate ? { FromDate: query.fromDate } : {}),
    ...(query.toDate ? { ToDate: query.toDate } : {}),
    ...(query.categories.length ? { Categories: [...query.categories] } : {}),
    ...(query.statuses.length ? { Statuses: [...query.statuses] } : {}),
    ...(query.zoneIds.length ? { ZoneIds: [...query.zoneIds] } : {}),
    ...(query.clientIds.length ? { ClientIds: [...query.clientIds] } : {}),
    PageNumber: query.page,
    PageSize: query.pageSize,
    SortBy: query.sort.field,
    SortDescending: query.sort.direction === 'desc',
  };
}

export function toDriverOption(
  dto: ExpenseDriverOptionDto
): ExpenseDriverOption {
  return {
    driverId: dto.driverId,
    fullName: dto.fullName ?? '',
  };
}
